import React, { useState, useEffect } from "react";
import { Card, Nav, Tab, Table, Button, Form, Badge } from "react-bootstrap";
import { db } from "../../../../firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";

import { useSalePackageOptions } from "../../Items/SalePackageItems/salePackageCatalog";

const OrderProductSelector = ({
  selectedProducts,
  onUpdateProducts,
  orderDetails,
  onUpdateOrderDetails,
}) => {
  const catalog = useSalePackageOptions((orderDetails && orderDetails.salePackage) || "");
  const [cpaps, setCpaps] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("cpaps");

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      // Load available CPAPs (both available and reserved)
      const cpapQuery = query(
        collection(db, "products"),
        where("status", "in", ["ว่าง", "จอง"]),
        orderBy("createdAt", "desc")
      );
      const cpapSnapshot = await getDocs(cpapQuery);
      const cpapList = cpapSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCpaps(cpapList);

      // Load available accessories
      const accessoryQuery = query(
        collection(db, "accessories"),
        where("stockQuantity", ">", 0),
        where("status", "==", "Active"),
        orderBy("category", "asc"),
        orderBy("itemName", "asc")
      );
      const accessorySnapshot = await getDocs(accessoryQuery);
      const accessoryList = accessorySnapshot.docs.map((doc) => ({
        id: doc.id,
        quantity: 1, // Default quantity
        maxQuantity: doc.data().stockQuantity,
        ...doc.data(),
      }));
      setAccessories(accessoryList);
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCpap = (cpap) => {
    const isSelected = selectedProducts.cpaps.some((c) => c.id === cpap.id);

    if (isSelected) {
      onUpdateProducts({
        ...selectedProducts,
        cpaps: selectedProducts.cpaps.filter((c) => c.id !== cpap.id),
      });
    } else {
      onUpdateProducts({
        ...selectedProducts,
        cpaps: [...selectedProducts.cpaps, { ...cpap }],
      });
    }
  };

  const handleSelectAccessory = (accessory) => {
    const isSelected = selectedProducts.accessories.some(
      (a) => a.id === accessory.id
    );

    if (isSelected) {
      onUpdateProducts({
        ...selectedProducts,
        accessories: selectedProducts.accessories.filter(
          (a) => a.id !== accessory.id
        ),
      });
    } else {
      onUpdateProducts({
        ...selectedProducts,
        accessories: [...selectedProducts.accessories, { ...accessory }],
      });
    }
  };

  const handleUpdateAccessoryQuantity = (accessoryId, quantity) => {
    const updatedAccessories = selectedProducts.accessories.map((acc) => {
      if (acc.id === accessoryId) {
        return { ...acc, quantity: Math.min(quantity, acc.maxQuantity) };
      }
      return acc;
    });

    onUpdateProducts({
      ...selectedProducts,
      accessories: updatedAccessories,
    });
  };

  const handleUpdateCpapDetails = (cpapId, field, value) => {
    const updatedCpaps = selectedProducts.cpaps.map((cpap) => {
      if (cpap.id === cpapId) {
        return { ...cpap, [field]: value };
      }
      return cpap;
    });

    onUpdateProducts({
      ...selectedProducts,
      cpaps: updatedCpaps,
    });
  };

  const renderCpapTab = () => (
    <div>
      {/* Selected CPAPs */}
      {selectedProducts.cpaps.length > 0 && (
        <Card className="mb-3 border-success">
          <Card.Header className="bg-success text-white">
            <h6 className="mb-0">
              Selected CPAPs ({selectedProducts.cpaps.length})
            </h6>
          </Card.Header>
          <Card.Body>
            {selectedProducts.cpaps.map((cpap) => (
              <div key={cpap.id} className="mb-3 p-3 border rounded">
                <div className="row">
                  <div className="col-md-4">
                    <strong>Serial:</strong> {cpap.serialNumber}
                    <br />
                    <strong>Brand:</strong> {cpap.brand}
                    <br />
                    <strong>Model:</strong> {cpap.model}
                  </div>
                  <div className="col-md-4"></div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={cpap.amount || ""}
                        onChange={(e) =>
                          handleUpdateCpapDetails(
                            cpap.id,
                            "amount",
                            e.target.value
                          )
                        }
                        placeholder="Enter amount"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Sale Package</Form.Label>
                      <Form.Control
                        as="select"
                        disabled={catalog.disabled}
                        value={(orderDetails && orderDetails.salePackage) || ""}
                        onChange={(e) =>
                          onUpdateOrderDetails("salePackage", e.target.value)
                        }
                      >
                        <option value="">Select Sale Package</option>
                        {catalog.options.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </Form.Control>
                      {catalog.message && <small className="text-danger">{catalog.message}</small>}
                    </Form.Group>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleSelectCpap(cpap)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Order Note */}
            <hr />
            <Form.Group>
              <Form.Label>Order Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={orderDetails.note || ""}
                onChange={(e) =>
                  onUpdateOrderDetails &&
                  onUpdateOrderDetails("note", e.target.value)
                }
                placeholder="Add any additional notes for this order..."
              />
            </Form.Group>
          </Card.Body>
        </Card>
      )}

      {/* Available CPAPs */}
      <Card>
        <Card.Header>
          <h6>Available CPAPs</h6>
        </Card.Header>
        <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Serial Number</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Sales</th>
                </tr>
              </thead>
              <tbody>
                {cpaps.map((cpap) => {
                  const isSelected = selectedProducts.cpaps.some(
                    (c) => c.id === cpap.id
                  );
                  return (
                    <tr
                      key={cpap.id}
                      className={isSelected ? "table-success" : ""}
                    >
                      <td>
                        <Button
                          variant={isSelected ? "success" : "outline-primary"}
                          size="sm"
                          onClick={() => handleSelectCpap(cpap)}
                        >
                          {isSelected ? "Selected" : "Select"}
                        </Button>
                      </td>
                      <td>{cpap.serialNumber}</td>
                      <td>{cpap.brand}</td>
                      <td>{cpap.model}</td>
                      <td>{cpap.description || "-"}</td>
                      <td>
                        <Badge
                          variant={
                            cpap.status === "ว่าง" ? "success" : "warning"
                          }
                        >
                          {cpap.status}
                        </Badge>
                      </td>
                      <td>
                        {cpap.status === "จอง" ? cpap.sales || "N/A" : "-"}
                      </td>
                    </tr>
                  );
                })}
                {cpaps.length === 0 && !loading && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No available CPAPs
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  const renderAccessoriesTab = () => (
    <div>
      {/* Selected Accessories */}
      {selectedProducts.accessories.length > 0 && (
        <Card className="mb-3 border-success">
          <Card.Header className="bg-success text-white">
            <h6 className="mb-0">
              Selected Accessories ({selectedProducts.accessories.length})
            </h6>
          </Card.Header>
          <Card.Body>
            {selectedProducts.accessories.map((accessory) => (
              <div
                key={accessory.id}
                className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
              >
                <div>
                  <strong>{accessory.itemName}</strong>
                  <br />
                  <small className="text-muted">
                    {accessory.category} - {accessory.brand}
                  </small>
                  <br />
                  <small>Available: {accessory.stockQuantity}</small>
                </div>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="number"
                    min="1"
                    max={accessory.maxQuantity}
                    value={accessory.quantity}
                    onChange={(e) =>
                      handleUpdateAccessoryQuantity(
                        accessory.id,
                        parseInt(e.target.value)
                      )
                    }
                    style={{ width: "80px", marginRight: "10px" }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleSelectAccessory(accessory)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* Available Accessories */}
      <Card>
        <Card.Header>
          <h6>Available Accessories</h6>
        </Card.Header>
        <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Brand</th>
                  <th>Size</th>
                  <th>Stock</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {accessories.map((accessory) => {
                  const isSelected = selectedProducts.accessories.some(
                    (a) => a.id === accessory.id
                  );
                  return (
                    <tr
                      key={accessory.id}
                      className={isSelected ? "table-success" : ""}
                    >
                      <td>
                        <Button
                          variant={isSelected ? "success" : "outline-primary"}
                          size="sm"
                          onClick={() => handleSelectAccessory(accessory)}
                        >
                          {isSelected ? "Selected" : "Select"}
                        </Button>
                      </td>
                      <td>{accessory.itemCode}</td>
                      <td>{accessory.itemName}</td>
                      <td>
                        <Badge variant="info">{accessory.category}</Badge>
                      </td>
                      <td>{accessory.brand}</td>
                      <td>{accessory.size || "N/A"}</td>
                      <td>
                        <Badge
                          variant={
                            accessory.stockQuantity <=
                            (accessory.minimumStock || 0)
                              ? "warning"
                              : "success"
                          }
                        >
                          {accessory.stockQuantity}
                        </Badge>
                      </td>
                      <td>฿{accessory.unitPrice || 0}</td>
                    </tr>
                  );
                })}
                {accessories.length === 0 && !loading && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No available accessories
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  return (
    <div>
      <h5>Step 2: Select Products</h5>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="cpaps">
              CPAP Devices
              {selectedProducts.cpaps.length > 0 && (
                <Badge variant="success" className="ml-2">
                  {selectedProducts.cpaps.length}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="accessories">
              Accessories
              {selectedProducts.accessories.length > 0 && (
                <Badge variant="success" className="ml-2">
                  {selectedProducts.accessories.length}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="cpaps">{renderCpapTab()}</Tab.Pane>
          <Tab.Pane eventKey="accessories">{renderAccessoriesTab()}</Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default OrderProductSelector;
